// Endpoints RTK Query da feature management. AnalyticsModule (Onda 3).
// Tipos manuais; FIXME quando openapi-typescript regenerar.
import { api } from '@/lib/api';
import { qs } from '@/lib/api/qs';
import type { PeriodPreset, ResolvedPeriodLabel } from '@/lib/filters/period';
import type { AvatarTone } from '@/components/ui/Avatar';
import type { Role } from '@/config/constants';
import type { MetricsDelta } from '@/features/dashboard';

export type LeaderboardSort = 'vendas' | 'csat' | 'done' | 'active';

/** Presets do filtro global de período da página /empresas (RF-26). */
export type ManagementPeriod = PeriodPreset;

/** Rótulo do envelope devolvido pelo back: presets + `custom` (from/to). */
export type ResolvedPeriod = ResolvedPeriodLabel;

/**
 * Filtro de período unificado (RF-26). `from`/`to` são date-only (YYYY-MM-DD) e
 * só valem quando os DOIS vêm juntos → o back resolve `period:'custom'`. Todos os
 * endpoints da página aceitam este shape além do `period` preset.
 */
export interface PeriodRange {
  period: ManagementPeriod;
  from?: string;
  to?: string;
}

export interface MetricsDto {
  period: ResolvedPeriod;
  from: string;
  to: string;
  /** Receita em centavos. */
  receita: number;
  atendimentos: number;
  /** RF-20/24: vendas concluídas (conclusionReason='SALE') no escopo. */
  vendasCount: number;
  /** Soma de saleValueCents/100 (REAIS) — fonte unificada de "vendas". */
  vendasBRL: number;
  /** Ticket médio em centavos. */
  ticketMedio: number;
  /** 0..1 fração resolvida pela IA. */
  taxaResolucaoIA: number;
  /** Qualidade média 0-100 avaliada pela IA (LLM-as-a-Judge) — "Qualidade (IA)".
   *  Opcional até o back popular. NÃO é o CSAT do cliente. */
  notaMedia?: number;
  /** CSAT real do cliente (1–5) agregado das lojas no escopo. 0 = sem respostas. */
  csat?: number;
  /** Nº de pesquisas de satisfação respondidas no período. */
  csatRespostas?: number;
  /** Respondidas / enviadas (0..1). */
  csatTaxaResposta?: number;
  deltas: {
    receita: MetricsDelta;
    atendimentos: MetricsDelta;
    ticketMedio: MetricsDelta;
    taxaResolucaoIA: MetricsDelta;
    csat?: MetricsDelta;
    vendasBRL?: MetricsDelta;
  };
}

export interface MetricsArgs extends PeriodRange {
  storeId?: string;
}

export interface HeatmapCellDto {
  /** 0=domingo, 6=sábado. */
  dayOfWeek: number;
  /** 0..23 (hora local de Brasília — RF-23). */
  hour: number;
  count: number;
}

export interface HeatmapDto {
  period: ResolvedPeriod;
  cells: HeatmapCellDto[];
}

export interface VendorRowDto {
  userId: string;
  name: string;
  role: Role;
  /** `null` quando o vendedor não tem loja resolvível. */
  storeId: string | null;
  storeName: string | null;
  /** Cor da loja já em hex (`#RRGGBB`) ou `null`. */
  storeColor: string | null;
  avatarTone: AvatarTone;
  /** URL da foto (proxy assinado) ou `null`. Ausente → só sigla. */
  avatarUrl?: string | null;
  online: boolean;
  atendimentos: number;
  /** Nº de vendas (conclusionReason='SALE') do vendedor no escopo. */
  vendasCount: number;
  /** Soma de saleValueCents/100 (REAIS) do vendedor no escopo. */
  vendasBRL: number;
  concluidos: number;
  ativos: number;
  /** CSAT real do cliente (1–5). 0 + csatRespostas=0 = sem avaliação → "—". */
  csat: number;
  /** Nº de pesquisas de satisfação respondidas no período. */
  csatRespostas?: number;
  performancePct: number;
  /** Qualidade média (0-100) avaliada pela IA — não é CSAT. */
  notaMedia: number;
  /**
   * Variação de posição no ranking vs. o período anterior. `>0` subiu (▲),
   * `<0` caiu (▼), `0` manteve, `null` = novo no ranking (sem posição anterior).
   */
  rankDelta: number | null;
  /**
   * Convite ainda não aceito: linha sintética (`userId: 'invite:<id>'`) anexada ao
   * fim do leaderboard, métricas zeradas. Ausente/false = usuário real. Nunca entra
   * no pódium (filtrado por `vendasCount > 0`); na tabela vira "Aguardando cadastro".
   */
  pending?: boolean;
}

export interface LeaderboardDto {
  period: ResolvedPeriod;
  sort: LeaderboardSort;
  data: VendorRowDto[];
}

export interface LeaderboardArgs extends MetricsArgs {
  sort: LeaderboardSort;
}

/** RF-26: um ponto da série de vendas/dia (bucket de dia local de Brasília). */
export interface SalesDailyPoint extends Record<string, unknown> {
  /** Dia local de Brasília no formato `YYYY-MM-DD`. */
  day: string;
  vendasCount: number;
  /** Valor em REAIS. */
  vendasBRL: number;
}

export interface SalesDailyDto {
  period: ResolvedPeriod;
  from: string;
  to: string;
  data: SalesDailyPoint[];
}

export interface StoreMetricsDto {
  /** PeriodEnvelope — back devolve junto, front não consome no StoreCard. */
  period?: ResolvedPeriod;
  from?: string;
  to?: string;
  storeId: string;
  storeName: string;
  city: string;
  color: string;
  /** Receita em centavos. */
  receita: number;
  atendimentos: number;
  /** Total all-time de conversas da loja (sem filtro de período). Opcional até o
   *  deploy da api; consumir com `?? atendimentos`. */
  atendimentosTotal?: number;
  active: number;
  done: number;
  /** RF-04: vendas concluídas (conclusionReason='SALE') da loja no período. */
  vendasCount?: number;
  /** RF-04: soma de saleValueCents/100 (REAIS) — fonte de "Receita no período". */
  vendasBRL?: number;
  /** RF-04: produtos distintos na tabela de preços DESTA loja (não company-wide). */
  productCount?: number;
  /** CSAT real do cliente (1–5) da loja no período. 0 = sem respostas. */
  csat?: number;
  /** Nº de pesquisas de satisfação respondidas no período. */
  csatRespostas?: number;
  vendedoresCount: number;
  /** ≤ 4 vendedores. */
  vendedoresPreview: { userId: string; name: string; avatarTone: AvatarTone }[];
  /**
   * RF-04: variações vs. período anterior. Opcional — tolera cache antigo do back
   * (pré-deploy do StoreMetricsDto novo). Usar sempre com `?? 0` no consumo.
   */
  deltas?: {
    receita?: MetricsDelta;
    atendimentos?: MetricsDelta;
    vendasBRL?: MetricsDelta;
  };
}

/** Args da série de vendas/dia — reusa o filtro global (period + from/to + loja). */
export type SalesDailyArgs = MetricsArgs;

/** Args do drill-down por loja: exige `storeId`, aceita o filtro de período. */
export interface StoreMetricsArgs extends PeriodRange {
  storeId: string;
}

export const managementApi = api.injectEndpoints({
  endpoints: (build) => ({
    metrics: build.query<MetricsDto, MetricsArgs>({
      query: ({ period, from, to, storeId }) => `/metrics${qs({ period, from, to, storeId })}`,
      providesTags: ['Metrics'],
    }),
    heatmap: build.query<HeatmapDto, MetricsArgs>({
      query: ({ period, from, to, storeId }) =>
        `/charts/heatmap${qs({ period, from, to, storeId })}`,
    }),
    leaderboard: build.query<LeaderboardDto, LeaderboardArgs>({
      query: ({ period, from, to, storeId, sort }) =>
        `/vendedores/leaderboard${qs({ period, from, to, storeId, sort })}`,
      providesTags: ['Leaderboard'],
    }),
    salesDaily: build.query<SalesDailyDto, SalesDailyArgs>({
      query: ({ period, from, to, storeId }) =>
        `/charts/sales-daily${qs({ period, from, to, storeId })}`,
    }),
    storeMetrics: build.query<StoreMetricsDto, StoreMetricsArgs>({
      query: ({ storeId, period, from, to }) =>
        `/stores/${storeId}/metrics${qs({ period, from, to })}`,
      providesTags: ['Metrics'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useMetricsQuery,
  useHeatmapQuery,
  useLeaderboardQuery,
  useSalesDailyQuery,
  useStoreMetricsQuery,
} = managementApi;
